import {isDate} from 'date-fns';
import {Chips, DatePicker, useTranslationContext} from 'platform/components';
import {Show, VStack} from 'platform/foundation';

import {getApiDateString, isApiDateString, parseDate, suffixTestId} from 'shared/web';

import {FILTER_VALUE_KEY} from '../../constants/filterValueKey';
import type {EagFilterProps} from '../../types/EagFilterProps';
import {getFilterFieldName} from '../../utils/getFilterFieldName';
import {makeOnChipsChange} from '../../utils/makeOnChipsChange';
import {parseDateFilter} from '../../utils/parseDateFilter';
import {getQuickFilterFromIntersection} from '../../utils/quickFilterValue';
import {QuickFilterChips} from '../QuickFilterChips/QuickFilterChips';

type DateFilterProps = EagFilterProps & {
  min: string | null;
  max: string | null;
  relativeDates: Array<{value: string; label: string}>;
  quickFilters?: Array<{value: string; label: string}>;
};

export function DateFilter(props: DateFilterProps) {
  const t = useTranslationContext();
  const minDate = parseDateFilter(props.min);
  const maxDate = parseDateFilter(props.max);

  const model = (props.value[FILTER_VALUE_KEY] as string | null) ?? null;

  const setFilterValue = (filterModel: Date | string | null) => {
    const newModel = isDate(filterModel) ? getApiDateString(filterModel) : filterModel;
    props.onChange({[FILTER_VALUE_KEY]: newModel});
  };

  const onChipsChange = makeOnChipsChange<Date | string | null>(setFilterValue, null);

  const datePickerValue = isApiDateString(model) ? parseDate(model) : null;
  const chipsValue = typeof model === 'string' ? [model] : [];

  const isQuickFilterValue = getQuickFilterFromIntersection(model);

  return (
    <VStack spacing={4}>
      <QuickFilterChips
        quickFilters={props.quickFilters}
        value={isQuickFilterValue}
        defaultValue={null}
        setFilterValue={setFilterValue}
        isDisabled={props.isDisabled}
        data-testid={suffixTestId('date-filter-quickFilters', props)}
      />
      <Show when={props.relativeDates && props.relativeDates.length > 0}>
        <Chips
          isDisabled={props.isDisabled}
          value={chipsValue}
          options={props.relativeDates}
          onChange={onChipsChange}
          data-testid={suffixTestId('date-filter-relativeDates', props)}
          isDeselectable
        />
      </Show>
      <DatePicker
        isDisabled={props.isDisabled}
        placeholder={t('page.datagrid.filter.datePlaceholder', {
          field: getFilterFieldName(props.column),
        })}
        value={datePickerValue}
        onChange={setFilterValue}
        minDate={minDate}
        maxDate={maxDate}
        data-testid={suffixTestId('date-filter', props)}
        stackDirection="column"
      />
    </VStack>
  );
}
